/**
 * useGymSetters Hook
 *
 * Custom hook for fetching all setters (users with setter or owner role)
 * at a specific gym. Used for multi-setter selection in route creation.
 */

import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFirebaseDb } from '@/lib/firebase';
import type { User, GymRole } from '@flash/shared';

/**
 * Setter information returned by the hook
 */
export interface SetterInfo {
  id: string;
  name: string;
  role: GymRole;
}

/**
 * State interface for gym setters query
 */
interface UseGymSettersState {
  /** List of setters at the gym */
  setters: SetterInfo[];
  /** Whether data is being loaded */
  loading: boolean;
  /** Error message if query failed */
  error: string | null;
}

/**
 * Return type for useGymSetters hook
 */
interface UseGymSettersResult extends UseGymSettersState {
  /** Refetch setters from Firestore */
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch setters (users with setter or owner role) at a gym
 *
 * @param gymId - The gym ID to get setters for
 * @returns Object containing setters list, loading state, and error
 *
 * @example
 * ```tsx
 * const { setters, loading, error } = useGymSetters(gymId);
 *
 * if (loading) return <LoadingSpinner />;
 *
 * return (
 *   <SetterChipsInput
 *     availableSetters={setters}
 *     selectedSetters={selectedSetters}
 *     onChange={setSelectedSetters}
 *   />
 * );
 * ```
 */
export const useGymSetters = (gymId: string | undefined): UseGymSettersResult => {
  const [state, setState] = useState<UseGymSettersState>({
    setters: [],
    loading: true,
    error: null,
  });

  /**
   * Fetch setters from Firestore
   */
  const fetchSetters = useCallback(async (): Promise<void> => {
    if (!gymId) {
      setState({ setters: [], loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const db = getFirebaseDb();

      // 1. Get all memberships for this gym
      const membershipsRef = collection(db, 'gymMemberships');
      const membershipsQuery = query(membershipsRef, where('gymId', '==', gymId));
      const membershipsSnapshot = await getDocs(membershipsQuery);

      // Filter to setters and owners
      const setterMemberships: Array<{ userId: string; role: GymRole }> = [];
      membershipsSnapshot.docs.forEach((docSnapshot) => {
        const data = docSnapshot.data();
        const role = data['role'] as GymRole;
        if (role === 'setter' || role === 'owner') {
          setterMemberships.push({
            userId: data['userId'] as string,
            role,
          });
        }
      });

      if (setterMemberships.length === 0) {
        setState({ setters: [], loading: false, error: null });
        return;
      }

      // 2. Get user details for each setter
      const userIds = setterMemberships.map((s) => s.userId);
      const usersRef = collection(db, 'users');

      // Firestore 'in' queries are limited to 30 items
      const userBatches: string[][] = [];
      for (let i = 0; i < userIds.length; i += 30) {
        userBatches.push(userIds.slice(i, i + 30));
      }

      const userMap = new Map<string, User>();
      for (const batch of userBatches) {
        const usersQuery = query(usersRef, where('id', 'in', batch));
        const usersSnapshot = await getDocs(usersQuery);
        usersSnapshot.docs.forEach((docSnapshot) => {
          const userData = docSnapshot.data() as User;
          userMap.set(userData.id, userData);
        });
      }

      // 3. Combine membership and user data
      const setters: SetterInfo[] = setterMemberships.map(({ userId, role }) => {
        const user = userMap.get(userId);
        return {
          id: userId,
          name: user?.displayName ?? 'Unknown User',
          role,
        };
      });

      // Sort: owners first, then by name
      setters.sort((a, b) => {
        if (a.role === 'owner' && b.role !== 'owner') return -1;
        if (b.role === 'owner' && a.role !== 'owner') return 1;
        return a.name.localeCompare(b.name);
      });

      setState({ setters, loading: false, error: null });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load setters';
      console.error('Error fetching gym setters:', err);
      setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, [gymId]);

  // Load setters when gymId changes
  useEffect(() => {
    fetchSetters();
  }, [fetchSetters]);

  return {
    ...state,
    refetch: fetchSetters,
  };
};

export default useGymSetters;
